import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { formatMeasurement } from '@klareospace/pharos-lib-front-utils';
import { VOLUME_UNITS, type Order, type VolumeUnit } from '@klareospace/pharos-lib-types';
import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@klareospace/pharos-lib-web-utils';

import { useInlineOrderFieldEdit } from '../hooks/use-inline-order-field-edit';
import type { OrderFieldEditState, OrderFieldLockReason } from '../hooks/use-order-editability';
import { ComputableValueCell } from './computable-value-cell';
import { EditableFieldWrapper } from './editable-field-wrapper';
import { InlineEditActions } from './inline-edit-actions';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface VolumeInlineEditorProps {
  readonly fieldState: OrderFieldEditState;
  readonly isError: boolean;
  readonly lockReason: OrderFieldLockReason | null;
  readonly order: Order;
}

type VolumeDraft = { unit: VolumeUnit; value: number };

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isValidVolume(draft: VolumeDraft): boolean {
  return Number.isFinite(draft.value) && draft.value > 0;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function VolumeInlineEditor({
  fieldState,
  isError,
  lockReason,
  order,
}: VolumeInlineEditorProps): React.JSX.Element {
  const { t } = useTranslation('common');
  const current = order.totalVolume?.data ?? { unit: VOLUME_UNITS.M3, value: 0 };

  const { draft, handleCancel, handleConfirm, isEditing, isPending, isValid, setDraft, startEdit } =
    useInlineOrderFieldEdit<VolumeDraft>({
      buildPatch: (value) => ({ totalVolume: value }),
      isValid: isValidVolume,
      order,
      successKey: 'drawer.edit.content.success',
      value: current,
    });

  if (isEditing) {
    return (
      <div className="mt-1 flex items-center gap-1">
        <Input
          className="h-7 flex-1 text-sm"
          disabled={isPending}
          min={0}
          onChange={(e): void => setDraft({ ...draft, value: Number(e.target.value) })}
          step="0.01"
          type="number"
          value={draft.value}
        />
        <Select
          disabled={isPending}
          onValueChange={(unit): void => setDraft({ ...draft, unit: unit as VolumeUnit })}
          value={draft.unit}
        >
          <SelectTrigger className="h-7 w-16 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.values(VOLUME_UNITS).map((unit) => (
              <SelectItem key={unit} value={unit}>
                {t(`units.${unit}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <InlineEditActions isPending={isPending} isValid={isValid} onCancel={handleCancel} onConfirm={handleConfirm} />
      </div>
    );
  }

  const volumeResult = formatMeasurement(order.totalVolume, (unit) => t(`units.${unit}`));
  const formatted = volumeResult ? volumeResult.text : '—';

  return (
    <EditableFieldWrapper fieldState={fieldState} lockReason={lockReason} onEdit={startEdit}>
      <ComputableValueCell formatted={formatted} isComputed={order.totalVolume?.isComputed} isError={isError} />
    </EditableFieldWrapper>
  );
}
